if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;

        this.hideErrors = this.dataset.hideErrors === 'true';
        // Aurala: prefer drawer when present. Default to drawer route on ATC;
        // only fall back to full-page /cart redirect if no drawer in DOM or
        // operator explicitly opts in via data-open-cart-on-add="true".
        const drawerEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const explicitRedirect = this.dataset.openCartOnAdd === 'true';
        this.cart = explicitRedirect ? null : drawerEl;
        this.openCartOnAdd = explicitRedirect || !drawerEl;

        if (drawerEl && !explicitRedirect) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        // Aurala: single-variant-per-product rule
        this.singleVariantRule = this.dataset.singleVariantRule === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Ensure we have a numeric product id if provided.
        const productId = this.dataset.productId ? parseInt(this.dataset.productId, 10) : null;

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        try {
          // Aurala: enforce single variant per product (replace other variants; increment same variant)
          if (this.singleVariantRule && productId && !Number.isNaN(productId)) {
            const handledResponse = await this.applySingleVariantRule(productId, formData);
            if (handledResponse) {
              this.processCartResponse(handledResponse, formData, evt);
              return;
            }
          }

          const response = await fetch(`${routes.cart_add_url}`, config).then((r) => r.json());
          this.processCartResponse(response, formData, evt);
        } catch (e) {
          console.error(e);
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');

          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      processCartResponse(response, formData, evt) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText?.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        }

        // Aurala: Shopify's Meta Pixel app isn't firing AddToCart reliably
        // (GA4 confirmed 2 ATCs on 2026-05-23, Meta pixel reported 0). Fire
        // manually here so Meta auto-optimisation can learn from ATC events.
        // Gated via AuralaPixelGate so dev/preview/automation can't pollute
        // the live pixel — see assets/aurala-pixel-gate.js.
        try {
          const gateAllow = window.AuralaPixelGate && window.AuralaPixelGate.allow();
          if (gateAllow && typeof window.fbq === 'function') {
            const item = response && response.items ? response.items[0] : response;
            const variantId = (item && item.id) || formData.get('id');
            const priceMinor = (item && (item.price ?? item.final_line_price)) || 0;
            const priceMajor = priceMinor ? priceMinor / 100 : 0;
            const qty = (item && item.quantity) || parseInt(formData.get('quantity') || '1', 10);
            const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
            window.fbq('track', 'AddToCart', {
              content_ids: [String(variantId)],
              content_type: 'product_variant',
              contents: [{ id: String(variantId), quantity: qty, item_price: priceMajor }],
              value: priceMajor * qty,
              currency: currency,
            });
            // Fire InitiateCheckout in the same beat — we're navigating straight
            // there. Meta optimisation gets both signals from one click.
            window.fbq('track', 'InitiateCheckout', {
              content_ids: [String(variantId)],
              content_type: 'product_variant',
              contents: [{ id: String(variantId), quantity: qty, item_price: priceMajor }],
              value: priceMajor * qty,
              currency: currency,
              num_items: qty,
            });
          } else if (!gateAllow && window.AuralaPixelGate) {
            console.info('[aurala] fbq AddToCart suppressed by pixel gate:', window.AuralaPixelGate.reason());
          }
        } catch (err) {
          console.warn('fbq AddToCart failed:', err);
        }

        // Aurala 2026-06-27: drawer-first flow restored. Removed the hard
        // window.location = '/cart' redirect that bypassed the drawer entirely.
        // Drawer now opens via the publish + cart-notification path below.

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => this.finishCartAction(response));
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => this.finishCartAction(response));
        }
      }

      finishCartAction(response) {
        if (this.openCartOnAdd) {
          window.location = window.routes ? window.routes.cart_url : '/cart';
          return;
        }

        if (this.cart) this.cart.dataset.openCartOnAdd = 'true';
        this.cart?.renderContents(response);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      // ===== Aurala helpers (single variant rule) =====
      async applySingleVariantRule(productId, formData) {
        const variantId = parseInt(formData.get('id'), 10);
        const qtyToAdd = Math.max(1, parseInt(formData.get('quantity') || '1', 10));
        if (!variantId || Number.isNaN(variantId)) return null;

        const sectionsRaw = formData.get('sections');
        const sectionsUrl = formData.get('sections_url') || window.location.pathname;

        const cart = await this.fetchCart();
        const items = (cart && Array.isArray(cart.items)) ? cart.items : [];
        const sameProductItems = items.filter((it) => it && String(it.product_id) === String(productId));

        const existingSameVariant = sameProductItems.find((it) => parseInt(it.id, 10) === variantId) || null;
        const otherVariants = sameProductItems.filter((it) => parseInt(it.id, 10) !== variantId);

        // Remove other variants first (no sections needed for intermediate state)
        for (let i = 0; i < otherVariants.length; i++) {
          await this.cartChange(otherVariants[i].key, 0, null, null);
        }

        // If same variant exists, increment via cart/change (so we don't add a new line)
        if (existingSameVariant) {
          const nextQty = (parseInt(existingSameVariant.quantity, 10) || 0) + qtyToAdd;
          const sectionsToRender = sectionsRaw ? String(sectionsRaw).split(',') : null;
          const resp = await this.cartChange(existingSameVariant.key, nextQty, sectionsToRender, sectionsUrl);
          resp.key = existingSameVariant.key;
          return resp;
        }

        // Let default /cart/add flow run
        return null;
      }

      async fetchCart() {
        const cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
        const url = String(cartUrl).endsWith('.js') ? cartUrl : `${cartUrl}.js`;
        const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        return await res.json();
      }

      async cartChange(idOrKey, quantity, sectionsToRender, sectionsUrl) {
        let cartChangeUrl = (window.routes && window.routes.cart_change_url) ? window.routes.cart_change_url : '/cart/change';
        if (!String(cartChangeUrl).endsWith('.js')) cartChangeUrl = `${cartChangeUrl}.js`;

        const body = new URLSearchParams();
        body.set('id', idOrKey);
        body.set('quantity', String(quantity));
        if (sectionsToRender && sectionsToRender.length) {
          body.set('sections', sectionsToRender.join(','));
          body.set('sections_url', sectionsUrl || window.location.pathname);
        }

        const res = await fetch(cartChangeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body
        });
        const parsed = await res.json();
        if (parsed.status) throw new Error(parsed.description || 'Unable to update cart.');
        return parsed;
      }
    }
  );
}
